import gql from 'graphql-tag';
import orderBy from 'lodash/orderBy';

import {
  MEDITATION_RESOURCE_TYPE,
  PROGRAM_RESOURCE_TYPE,
  BENEFIT_RESOURCE_TYPE,
} from '../constants';

const createItem = (item, resourceType, resourceMap) => {
  const uniqueKey = `${item.id}_${resourceType}`;
  return {
    ...item,
    uniqueKey,
    resourceType,
    isSelected: resourceMap[uniqueKey],
  };
};

/*
    Logic for handling adding resources to chat messages.
*/
export const resources = {
  state: {
    resourcesToAdd: [],
    meditations: [],
    programs: [],
    companyAdditionalBenefits: [],
    showResourceInfoPanel: false,
    selectedItem: {},
  },
  reducers: {
    setResources(state, resourcesToAdd) {
      return { ...state, resourcesToAdd };
    },
    setMeditationsAndPrograms(state, meditations, programs) {
      return { ...state, meditations, programs };
    },
    setMemberFeaturedResources(state, memberFeaturedResources) {
      return {
        ...state,
        ...memberFeaturedResources,
      };
    },
    reset(state) {
      return { ...state, resourcesToAdd: [] };
    },
    'chat/setCurrentConversation': (state) => {
      return { ...state, resourcesToAdd: [] };
    },
    toogleShowResourceInfoPanel(state) {
      return { ...state, showResourceInfoPanel: !state.showResourceInfoPanel };
    },
    setSelectedItem(state, selectedItem) {
      return { ...state, selectedItem };
    },
  },
  effects: () => ({
    async fetchResources(payload, rootState) {
      // Call this only once when initializing the app
      const response = await rootState.network.memberClient.query({
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        query: getMessageResources,
        fetchPolicy: 'network-only',
      });

      const { meditations, programs } = response.data;
      this.setMeditationsAndPrograms(meditations, programs);
    },
    async fetchCompanyAdditionalBenefits(payload, rootState) {
      // Call this for every client selection
      const response = await rootState.network.memberClient.query({
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        query: getMemberFeaturedResources,
        variables: { uuid: payload },
        fetchPolicy: 'network-only',
      });

      const { companyAdditionalBenefits } = response.data.member;
      const { memberFeaturedMeditations, memberFeaturedPrograms } = response.data;
      this.setMemberFeaturedResources({
        companyAdditionalBenefits,
        memberFeaturedMeditations,
        memberFeaturedPrograms,
      });
    },
    toggleResource(item, rootState) {
      const newResources = [...rootState.resources.resourcesToAdd];
      const index = newResources.findIndex((resource) => resource.uniqueKey === item.uniqueKey);
      if (index >= 0) {
        newResources.splice(index, 1);
      } else {
        newResources.push(item);
      }
      this.setResources(newResources);
    },
  }),
  selectors: (slice, createSelector) => ({
    resourceMap() {
      return createSelector(
        (state) => state.resources.resourcesToAdd,
        (resourcesToAdd) => {
          const resourceMap = {};
          resourcesToAdd.forEach((resource) => {
            resourceMap[resource.uniqueKey] = resource;
          });
          return resourceMap;
        },
      );
    },
    meditationMap() {
      return createSelector(
        (state) => state.resources.meditations,
        (meditations) => {
          const meditationMap = {};
          meditations.forEach((med) => {
            meditationMap[med.id] = med;
          });
          return meditationMap;
        },
      );
    },
    programMap() {
      return createSelector(
        (state) => state.resources.programs,
        (programs) => {
          const programMap = {};
          programs.forEach((prog) => {
            programMap[prog.id] = prog;
          });
          return programMap;
        },
      );
    },
    benefitMap() {
      return createSelector(
        (state) => state.resources.companyAdditionalBenefits,
        (companyAdditionalBenefits) => {
          const benefitMap = {};
          companyAdditionalBenefits.forEach((benefit) => {
            benefitMap[benefit.id] = benefit;
          });
          return benefitMap;
        },
      );
    },
    decoratedMeditations() {
      return createSelector(
        (state) => state.resources.meditations,
        (state) => state.resources.memberFeaturedMeditations,
        this.resourceMap,
        (meditations, memberFeaturedMeditations, resourceMap) => {
          return orderBy(
            meditations.map((meditation) => {
              let topicsInCommon = 0;
              if (memberFeaturedMeditations) {
                topicsInCommon = memberFeaturedMeditations.some((x) => x.id === meditation.id) && 1;
              }

              return createItem(
                {
                  ...meditation,
                  topicsInCommon,
                },
                MEDITATION_RESOURCE_TYPE,
                resourceMap,
              );
            }),
            ['topicsInCommon', 'order'],
            ['desc', 'asc'],
          );
        },
      );
    },
    decoratedPrograms() {
      return createSelector(
        (state) => state.resources.programs,
        (state) => state.resources.memberFeaturedPrograms,
        this.resourceMap,
        (programs, memberFeaturedPrograms, resourceMap) => {
          return orderBy(
            programs.map((program) => {
              let topicsInCommon = 0;
              if (memberFeaturedPrograms) {
                topicsInCommon = memberFeaturedPrograms.some((x) => x.id === program.id) && 1;
              }

              return createItem(
                {
                  ...program,
                  topicsInCommon,
                },
                PROGRAM_RESOURCE_TYPE,
                resourceMap,
              );
            }),
            ['topicsInCommon', 'order'],
            ['desc', 'asc'],
          );
        },
      );
    },
    decoratedBenefits() {
      return createSelector(
        (state) => state.resources.companyAdditionalBenefits,
        this.resourceMap,
        (companyAdditionalBenefits, resourceMap) => {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          return companyAdditionalBenefits.map((benefit) =>
            createItem(benefit, BENEFIT_RESOURCE_TYPE, resourceMap),
          );
        },
      );
    },
  }),
};

const getMessageResources = gql`
  query getMessageResources {
    meditations {
      id
      name
      image
    }
    programs {
      id
      name
      image
    }
  }
`;

const getMemberFeaturedResources = gql`
  query GetMemberInformation($uuid: UUID!) {
    member(uuid: $uuid) {
      companyAdditionalBenefits {
        id
        name: title
        description
        providerName
        link
        image
        color
        topicsInCommon
        topics {
          uuid
          name
        }
      }
    }
    memberFeaturedMeditations(uuid: $uuid) {
      id
    }
    memberFeaturedPrograms(uuid: $uuid) {
      id
    }
  }
`;
